/**
 * Push tap-routing — the native tap → in-app navigate driver
 * (push-notifications-v2.md §5.3).
 *
 * Renders nothing; mounted once at the app root (beside NativeLinkGuard /
 * HealthSyncMount) so it lives across the whole native session and is already
 * present during the boot route ('/') to catch a COLD-START tap (a tap that
 * launched the app).
 *
 * Wiring is native + `push_notifications_v1` gated: on web (the sw.js lane owns
 * taps there), when the flag is OFF, or when the plugin can't bind, this is a
 * pure no-op — no listener, no navigation. With push_notifications_v1 OFF (the
 * current state) the whole surface is inert.
 *
 * Cold-start ordering: a tap fires `pushNotificationActionPerformed` as soon as
 * the listener attaches — but NativeRoot is still on '/' and about to
 * replace-navigate to /today. So the resolved route is QUEUED and flushed only
 * once the router has left the boot root (`isRouterReadyForTap`). A warm tap
 * (app already on a real route) flushes immediately.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useServerFlag } from '../../hooks/useServerFlag';
import { isNativeApp } from '../../lib/platform';
import { registerPushTapListener } from '../../lib/nativePush';
import { isRouterReadyForTap, resolveQueuedTap } from '../../lib/pushTapQueue';
import { activationStateForPushRoute } from '../../lib/run/runContextActivation';

export function PushTapMount(): null {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading } = useAuthContext();
  const pushOn = useServerFlag('push_notifications_v1');

  // A cold-start route waiting for boot to hand off. Held in a ref so the
  // listener callback (wired once) always reads the latest without re-subscribing.
  const queuedRef = useRef<string | null>(null);

  // Router readiness (auth resolved + left the boot root). A ref keeps it
  // reachable from the listener closure; the state drives the flush effect.
  const [ready, setReady] = useState(false);
  const readyRef = useRef(false);
  readyRef.current = ready;

  // Flush a queued route the moment the router is ready. Stable (navigate only),
  // so wiring the listener does not depend on `ready` and never re-subscribes.
  const flush = useCallback(() => {
    const { navigateTo, nextQueued } = resolveQueuedTap({
      routerReady: readyRef.current,
      queued: queuedRef.current,
    });
    queuedRef.current = nextQueued;
    if (navigateTo == null) return;
    // A post-run reaction tap carries `run=` and must open the RUN-AWARE
    // conversation, not an unseeded one — so it mints the §2.1 activation
    // here (see `activationStateForPushRoute` for why a push tap is a lawful
    // minting path and the anchor tap is not the only one). Any other route
    // returns null and navigates exactly as before.
    const state = activationStateForPushRoute(navigateTo);
    navigate(navigateTo, state ? { state } : undefined);
  }, [navigate]);

  // Detect the boot handoff: once auth has resolved and the shell has left '/',
  // the router can honor a tap route without NativeRoot clobbering it.
  useEffect(() => {
    if (ready) return;
    if (isRouterReadyForTap({ authLoading: loading, pathname: location.pathname })) {
      setReady(true);
    }
  }, [ready, loading, location.pathname]);

  // When readiness flips true, drain any queued cold-start route.
  useEffect(() => {
    if (ready) flush();
  }, [ready, flush]);

  // Wire the native tap listener (native + flag gated). Inert everywhere else.
  useEffect(() => {
    if (!isNativeApp() || !pushOn) return;
    let cancelled = false;
    let detach: (() => void) | null = null;
    void registerPushTapListener((route) => {
      queuedRef.current = route;
      flush(); // navigates now if ready; otherwise leaves it queued for boot
    }).then((d) => {
      if (cancelled) {
        d();
        return;
      }
      detach = d;
    });
    return () => {
      cancelled = true;
      detach?.();
    };
  }, [pushOn, flush]);

  return null;
}
